import React, { useState, Fragment } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Card, CardContent, Grid, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, useTheme, useMediaQuery,
  List, ListItem, ListItemText, Divider, Accordion, AccordionSummary,
  AccordionDetails, ButtonGroup
} from '@mui/material';
import { AddCircle, Delete, Edit, PlayArrow, AutoStories, ExpandMore } from '@mui/icons-material';
import { booksAPI } from '../../services/api';
import TextGenerationDialog from './TextGenerationDialog';

const TEXT_TYPES = {
  'original': 'Original',
  'short': 'Short Version',
  'medium': 'Medium Version',
  'long': 'Long Version'
};

// Standard versions for empty state placeholders
const STANDARD_DURATIONS = [
  { type: 'short', name: 'Short Version', minutes: 15, icon: '🕒' },
  { type: 'medium', name: 'Medium Version', minutes: 30, icon: '🕒' },
  { type: 'long', name: 'Long Version', minutes: 60, icon: '🕒' },
];

// Add utility function to count words
const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

export default function TextVersionsPanel({ bookId, bookTexts, setBookTexts, setSnackbar, onTextSelect }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTextId, setEditingTextId] = useState(null);
  const [formData, setFormData] = useState({
    type: 'original',
    language: 'en',
    textContent: '',
    provider: 'manual'
  });

  // Generation dialog state
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('short');
  const [generatingType] = useState(null);

  // Add new state for generation defaults
  const [generationDefaults, setGenerationDefaults] = useState({
    generationType: 'standard',
    wordCount: 2500,
    structuredModel: 'openai'
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Prepare the data object with bookId
      const data = {
        ...formData,
        bookId
      };

      let response;

      if (isEditing && editingTextId) {
        // Update existing text
        response = await booksAPI.updateBookText(editingTextId, data);
        setSnackbar({
          open: true,
          message: 'Text version updated successfully',
          severity: 'success'
        });
      } else {
        // Create new text
        response = await booksAPI.createBookText(data);
        setSnackbar({
          open: true,
          message: 'New text version added successfully',
          severity: 'success'
        });
      }

      // Refresh the text versions list
      const textsResponse = await booksAPI.getBookTexts(bookId);
      setBookTexts(textsResponse.data);

      // Close dialog and reset form
      handleCloseDialog();

    } catch (error) {
      console.error('Error saving text version:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save text version',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteText = async (textId) => {
    if (!window.confirm('Are you sure you want to delete this text version?')) {
      return;
    }

    try {
      setLoading(true);
      await booksAPI.deleteBookText(textId);

      // Refresh the text versions list
      const textsResponse = await booksAPI.getBookTexts(bookId);
      setBookTexts(textsResponse.data);

      setSnackbar({
        open: true,
        message: 'Text version deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting text version:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete text version',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (isEdit = false, textVersion = null) => {
    if (isEdit && textVersion) {
      setIsEditing(true);
      setEditingTextId(textVersion.id);
      setFormData({
        type: textVersion.type,
        language: textVersion.language,
        textContent: textVersion.textContent,
        provider: textVersion.provider || 'manual'
      });
    } else {
      setIsEditing(false);
      setEditingTextId(null);
      setFormData({
        type: 'original',
        language: 'en',
        textContent: '',
        provider: 'manual'
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditing(false);
    setEditingTextId(null);
    setFormData({
      type: 'original',
      language: 'en',
      textContent: '',
      provider: 'manual'
    });
  };

  const handleOpenGenerationDialog = (type) => {
    setSelectedDuration(type);

    // Set appropriate defaults based on duration
    let defaults = {
      generationType: 'standard',
      structuredModel: 'openai'
    };

    setGenerationDefaults(defaults);
    setGenerationDialogOpen(true);
  };

  const handleGenerationSuccess = async () => {
    setSnackbar({
      open: true,
      message: 'Text generation started successfully',
      severity: 'success'
    });

    // Refresh data after successful generation request
    try {
      const textsResponse = await booksAPI.getBookTexts(bookId);
      setBookTexts(textsResponse.data);
    } catch (error) {
      console.error('Error refreshing text versions:', error);
    }
  };

  const handleGenerationError = (error) => {
    setSnackbar({
      open: true,
      message: `Failed to generate text: ${error.message || 'Unknown error'}`,
      severity: 'error'
    });
  };

  // Check if we have any text versions at all
  const hasTexts = bookTexts && bookTexts.length > 0;

  // Get available durations from existing texts
  const availableDurations = bookTexts.reduce((acc, text) => {
    if (text.type !== 'original' && !acc.includes(text.type)) {
      acc.push(text.type);
    }
    return acc;
  }, []);

  // New function to render mobile view of text versions
  const renderMobileVersionsList = () => {
    if (!hasTexts) return null;

    return (
      <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
        {bookTexts.map((text) => (
          <Fragment key={text.id}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`panel-${text.id}-content`}
                id={`panel-${text.id}-header`}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                    {TEXT_TYPES[text.type] + ', id: ' + text.id}
                  </Typography>
                  <Chip
                    label={`${countWords(text.textContent)} words`}
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {text.language.toUpperCase()} • {text.provider || 'manual'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    mb: 2
                  }}
                >
                  {text.textContent}
                </Typography>
                <ButtonGroup size="small" fullWidth>
                  <Button 
                    onClick={() => onTextSelect(text)} 
                    color="primary"
                    startIcon={<PlayArrow />}
                  >
                    Audio
                  </Button>
                  <Button
                    onClick={() => handleOpenDialog(true, text)}
                    color="info"
                    startIcon={<Edit />}
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDeleteText(text.id)} 
                    color="error"
                    startIcon={<Delete />}
                  >
                    Delete
                  </Button>
                </ButtonGroup>
              </AccordionDetails>
            </Accordion>
            <Divider />
          </Fragment>
        ))}

        {/* Placeholders for missing standard durations */}
        {STANDARD_DURATIONS.map((duration) => {
          if (!bookTexts.some(text => text.type === duration.type)) {
            return (
              <ListItem 
                key={`mobile-placeholder-${duration.type}`}
                sx={{ bgcolor: 'rgba(0,0,0,0.02)', py: 2 }}
                secondaryAction={
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenGenerationDialog(duration.type)}
                  >
                    Generate
                  </Button>
                }
              >
                <ListItemText 
                  primary={duration.name} 
                  secondary="Not available"
                />
              </ListItem>
            );
          }
          return null;
        })}
      </List>
    );
  };

  // Mobile empty state cards
  const renderMobileEmptyState = () => (
    <Grid container spacing={2}>
      {STANDARD_DURATIONS.map((duration) => (
        <Grid item xs={12} key={duration.type}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" component="div" gutterBottom>
                {duration.icon} {duration.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {duration.minutes} min read/listen
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={generatingType === duration.type ? <CircularProgress size={16} color="inherit" /> : <AutoStories />}
                onClick={() => handleOpenGenerationDialog(duration.type)}
                disabled={generatingType === duration.type}
                fullWidth
                sx={{ mt: 1 }}
              >
                {generatingType === duration.type ? 'Generating...' : 'Generate Text'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: 1,
        mb: 2 
      }}>
        <Typography variant="h6">Available Text Versions</Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<AddCircle />}
            onClick={() => handleOpenDialog(false)}
            fullWidth={isMobile}
          >
            Add Manual
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoStories />}
            onClick={() => handleOpenGenerationDialog(15)}
            fullWidth={isMobile}
          >
            Generate Text
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !hasTexts ? (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            No text versions available. Add one manually or generate text using AI.
          </Alert>
          
          {/* Responsive empty state */}
          {isMobile ? renderMobileEmptyState() : (
            <Grid container spacing={3}>
              {STANDARD_DURATIONS.map((duration) => (
                <Grid item xs={12} md={4} key={duration.type}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box component="span" mr={1}>{duration.icon}</Box>
                        {duration.name}
                      </Typography>
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        AI-generated summary that takes approximately {duration.minutes} minutes to read or listen to.
                      </Typography>
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={generatingType === duration.type ? <CircularProgress size={16} color="inherit" /> : <AutoStories />}
                          onClick={() => handleOpenGenerationDialog(duration.type)}
                          disabled={generatingType === duration.type}
                          fullWidth
                        >
                          {generatingType === duration.type ? 'Generating...' : 'Generate Text'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : (
        // Conditional rendering based on screen size
        isMobile ? renderMobileVersionsList() : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Text Preview</TableCell>
                  <TableCell>Words</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookTexts.map((text) => (
                  <TableRow key={text.id}>
                    <TableCell>{(TEXT_TYPES[text.type] || text.type) + ', id: ' + text.id }</TableCell>
                    <TableCell>{text.language}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {text.textContent}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${countWords(text.textContent)} words`}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{text.provider || 'manual'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleOpenDialog(true, text)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Generate Audio">
                        <IconButton 
                          onClick={() => onTextSelect(text)} 
                          color="primary" 
                          size="small"
                        >
                          <PlayArrow fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          onClick={() => handleDeleteText(text.id)} 
                          color="error"
                          size="small"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Additional rows for missing standard durations */}
                {STANDARD_DURATIONS.map((duration) => {
                  if (!bookTexts.some(text => text.type === duration.type)) {
                    return (
                      <TableRow key={`placeholder-${duration.type}`} sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                        <TableCell>{duration.name}</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            Not available
                          </Typography>
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleOpenGenerationDialog(duration.type)}
                          >
                            Generate
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return null;
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* Add/Edit Text Dialog - make it fullScreen on mobile */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {isEditing ? 'Edit Text Version' : 'Add New Text Version'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2, 
              mb: 2 
            }}>
              <FormControl fullWidth>
                <InputLabel id="text-type-label">Type</InputLabel>
                <Select
                  labelId="text-type-label"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Type"
                  disabled={isEditing}
                >
                  <MenuItem value="original">Original</MenuItem>
                  <MenuItem value="short">Short Version</MenuItem>
                  <MenuItem value="medium">Medium Version</MenuItem>
                  <MenuItem value="long">Long Version</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="language-label">Language</InputLabel>
                <Select
                  labelId="language-label"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  label="Language"
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                  <MenuItem value="it">Italian</MenuItem>
                  <MenuItem value="pt">Portuguese</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Text Content"
              name="textContent"
              value={formData.textContent}
              onChange={handleInputChange}
              multiline
              rows={isMobile ? 8 : 12}
              fullWidth
              required
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel id="provider-label">Provider</InputLabel>
              <Select
                labelId="provider-label"
                name="provider"
                value={formData.provider}
                onChange={handleInputChange}
                label="Provider"
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="vertex-ai">Google Vertex AI</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !formData.textContent}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Text Generation Dialog Component */}
      <TextGenerationDialog
        open={generationDialogOpen}
        onClose={() => setGenerationDialogOpen(false)}
        bookId={bookId}
        lengthType={selectedDuration}
        onSuccess={handleGenerationSuccess}
        onError={handleGenerationError}
        defaultGenerationType={generationDefaults.generationType}
        defaultWordCount={generationDefaults.wordCount}
        defaultStructuredModel={generationDefaults.structuredModel}
        fullScreen={isMobile}
      />
    </Paper>
  );
}
